import type { Rect } from "../../common/geometry.js";
import { parseMarkup, type XmlElement } from "../markup/xml.js";
import type { DrawableTexture, RenderContext } from "../render/RenderContext.js";
import type { WebwinImage } from "../../engine/ImageStore.js";
import type { Theme } from "../themes/Theme.js";
import { ArtControl, resolveColorRef, type ArtOptions } from "./ArtControl.js";
import {
  compileDrawing,
  getArtDefinition,
  onArtDefinitionsChanged,
  paintDefinition,
  paintDrawing,
  parseViewBox,
  fitMatrix,
  rootScope,
  type ArtEnvironment,
  type Drawing,
} from "./markup/drawing.js";
import { compileExpression, toNumber, type Expression, type Value } from "./markup/expr.js";
import "./library/index.js";

export type ArtFit = "contain" | "cover" | "stretch" | "none";

/**
 * Where a drawing's expressions find variables: a markup document's `VariableStore` is one, and a
 * host with variables of its own - a page's state - passes the same three things.
 */
export interface ArtVariables {
  has?(name: string): boolean;
  /** What the variable shows: a number, a flag, a color written as text. */
  shown(name: string): number | string | boolean | undefined;
  /** Hear any change; returns the way to stop. */
  onChange(listener: (name: string) => void): () => void;
}

/** A parameter as a caller hands it: a number, or text read as an expression - `"accent"`, `"t*2"`. */
export type ArtParam = number | boolean | string;

export interface ArtViewOptions extends ArtOptions {
  /** A drawing: its elements, or a whole `<Art>` element around them. */
  markup?: string;
  /** A drawing already compiled. */
  drawing?: Drawing;
  /** A `<Define>` to draw instead of a drawing of its own. */
  ref?: string | null;
  /** The definition's parameters, by name. */
  params?: Record<string, ArtParam>;
  /** The drawing's own units: "x y width height", fitted into the control. */
  viewBox?: Rect | string | null;
  fit?: ArtFit;
  /** A document's variables, so the drawing's expressions can name them. */
  variables?: ArtVariables | null;
  /** Px the drawing may paint past its box - a glow, a halo. */
  overflow?: number;
}

/**
 * A drawing written in art markup: shapes, generators and expressions of time, painted by the art
 * clock. `ref` draws a registered `<Define>` - the shipped library has `starfield`, `globe`,
 * `sigil` and the rest - with `params`; otherwise it draws its own `markup`.
 *
 * Each parameter is a real property on the control as well, once the definition is known, so a
 * markup `<Link prop="fire" variable="fire"/>` reaches one exactly as it reaches any other
 * property. A parameter can also simply name the variable - `fire="fire"` - since expressions read
 * the document's variables.
 */
export class ArtView extends ArtControl {
  #drawing: Drawing | null;
  #ref: string | null;
  #params = new Map<string, Expression>();
  #viewBox: Rect | null;
  fit: ArtFit;
  overflow: number;
  #variables: ArtVariables | null;
  #stopVariables: (() => void) | null = null;
  #stopDefinitions: (() => void) | null = null;
  #images = new Map<string, WebwinImage | null>();
  #played = new Map<string, number>();
  #memory = new Map<object, { value: number; at: number }>();

  constructor(options: ArtViewOptions = {}) {
    super(options);
    this.#ref = options.ref ?? null;
    this.#viewBox = typeof options.viewBox === "string" ? parseViewBox(options.viewBox, { line: 0, col: 0 }) : (options.viewBox ?? null);
    this.fit = options.fit ?? "contain";
    this.overflow = options.overflow ?? 0;
    this.#variables = options.variables ?? null;
    this.#drawing = options.drawing ?? (options.markup ? ArtView.compile(options.markup) : null);
    for (const [k, v] of Object.entries(options.params ?? {})) this.#params.set(k, toExpression(v));
    this.#exposeParams();
    this.#stopVariables = this.#variables?.onChange(() => this.invalidate()) ?? null;
    this.#stopDefinitions = onArtDefinitionsChanged(() => {
      this.#exposeParams();
      this.syncClock();
      this.invalidate();
    });
  }

  /** Compile markup - drawing elements, or an `<Art>` element holding them. */
  static compile(markup: string): Drawing {
    const root = parseMarkup(`<Art>${markup}</Art>`);
    const only = root.children.length === 1 && root.children[0]!.name === "Art" ? root.children[0]! : root;
    return compileDrawing(only.children);
  }

  get ref(): string | null {
    return this.#ref;
  }
  set ref(v: string | null) {
    this.#ref = v;
    this.#exposeParams();
    this.syncClock();
    this.invalidate();
  }

  get drawing(): Drawing | null {
    return this.#drawing;
  }
  set drawing(v: Drawing | null) {
    this.#drawing = v;
    this.syncClock();
    this.invalidate();
  }

  get viewBox(): Rect | null {
    return this.#viewBox;
  }
  set viewBox(v: Rect | null) {
    this.#viewBox = v;
    this.invalidate();
  }

  /** Set one parameter: a number, or text read as an expression. */
  setParam(name: string, value: ArtParam | null): void {
    if (value === null) this.#params.delete(name);
    else this.#params.set(name, toExpression(value));
    this.syncClock();
    this.invalidate();
  }

  /** The parameters as given, by name. */
  get params(): Record<string, string> {
    return Object.fromEntries([...this.#params].map(([k, e]) => [k, e.source]));
  }

  /** Start the manual `<Animate name>` animations of that name. */
  play(name: string): void {
    this.#played.set(name, this.time);
    this.syncClock();
    this.invalidate();
  }

  /** Let go of what it listens to. */
  dispose(): void {
    this.#stopVariables?.();
    this.#stopDefinitions?.();
    this.#stopVariables = this.#stopDefinitions = null;
  }

  protected override get moves(): boolean {
    if (this.#played.size > 0) return true;
    if ([...this.#params.values()].some((e) => e.names.has("t"))) return true;
    const def = this.#ref ? getArtDefinition(this.#ref) : null;
    return (def?.drawing.animated ?? false) || (this.#drawing?.animated ?? false);
  }

  override get paintOverflow(): number {
    const def = this.#ref ? getArtDefinition(this.#ref) : null;
    let extra = this.overflow;
    if (def?.overflow) {
      const v = toNumber(def.overflow.evaluate((n) => (n === "w" ? this.width : n === "h" ? this.height : undefined)));
      if (Number.isFinite(v)) extra = Math.max(extra, v);
    }
    return Math.max(super.paintOverflow, extra);
  }

  protected override paintArt(rc: RenderContext, theme: Theme, t: number): void {
    const def = this.#ref ? getArtDefinition(this.#ref) : null;
    const env: ArtEnvironment = {
      rc,
      theme,
      t,
      w: this.width,
      h: this.height,
      seed: this.seed,
      lookup: (name) => this.#lookup(name, theme),
      memory: this.#memory,
      played: (name) => this.#played.get(name),
      image: (src) => this.#image(src),
    };
    if (def) {
      const root = rootScope(env);
      const given: Record<string, Value> = {};
      for (const [k, e] of this.#params) given[k] = e.evaluate(root);
      paintDefinition(def, given, env, this.fit);
      return;
    }
    if (!this.#drawing) return;
    if (this.#viewBox) env.matrix = fitMatrix(this.#viewBox, this.width, this.height, this.fit);
    paintDrawing(this.#drawing, env);
  }

  #lookup(name: string, theme: Theme): Value | undefined {
    if (name === "reveal") return this.drawProgress;
    const store = this.#variables;
    if (!store || (store.has && !store.has(name))) return undefined;
    const v = store.shown(name);
    if (typeof v === "number") return v;
    if (typeof v === "boolean") return v ? 1 : 0;
    if (typeof v === "string") {
      const color = resolveColorRef(v, theme.palette);
      if (color) return color;
      const n = Number(v);
      return Number.isFinite(n) ? n : undefined;
    }
    return undefined;
  }

  #image(src: string): DrawableTexture | null {
    if (this.#images.has(src)) return this.#images.get(src) ?? null;
    const desktop = this.desktop;
    if (!desktop) return null;
    this.#images.set(src, null);
    desktop.images.load(src).then(
      (image) => {
        this.#images.set(src, image);
        this.invalidate();
      },
      () => {},
    );
    return null;
  }

  /** Each of the definition's parameters as a property, so a `<Link>` can reach it by name. */
  #exposeParams(): void {
    const def = this.#ref ? getArtDefinition(this.#ref) : null;
    if (!def) return;
    for (const p of def.params) {
      if (p.name in this && !Object.hasOwn(this, p.name)) continue;
      if (Object.hasOwn(this, p.name)) continue;
      Object.defineProperty(this, p.name, {
        configurable: true,
        enumerable: false,
        get: () => this.#params.get(p.name)?.source ?? p.value.source,
        set: (v: ArtParam) => this.setParam(p.name, v),
      });
    }
  }
}

function toExpression(v: ArtParam): Expression {
  if (typeof v === "boolean") return compileExpression(v ? "1" : "0");
  if (typeof v === "number") return compileExpression(Number.isFinite(v) ? String(v) : "0");
  return compileExpression(v);
}

/** The drawing elements of an `<Art>` in a document, compiled. */
export function drawingOf(children: readonly XmlElement[]): Drawing {
  return compileDrawing(children);
}
